import React, { useEffect, useMemo, useRef, useState } from 'react';
import { array, object, string } from 'prop-types';
import MultiTouch from 'mapbox-gl-multitouch';

import { useConfiguration } from '../../context/configurationContext';
import { isMapsLibLoaded } from '../Map/MapboxMap';
import {
  POI_MARKER_COLORS,
  POI_MARKER_LABELS,
  buildPoiMarkersForSubpage,
  formatPoiDistance,
  getPoiMapHeading,
  shouldShowPoiMap,
} from './seoPoiMapUtils';
import css from './SeoDiscovery.module.css';

const popupHtml = marker => {
  const distance = formatPoiDistance(marker.distanceMeters);
  const kindLabel = POI_MARKER_LABELS[marker.kind] || 'Place';
  const distanceLine = distance && marker.kind !== 'venue' ? `<div>${distance} from venue</div>` : '';
  return `<strong>${marker.label}</strong><div>${kindLabel}</div>${distanceLine}`;
};

const createMarkerElement = kind => {
  const el = document.createElement('div');
  el.className = css.poiMapMarker;
  el.style.setProperty('--poi-marker-color', POI_MARKER_COLORS[kind] || '#666666');
  return el;
};

const PoiMapLegend = ({ markers }) => {
  const kinds = [...new Set(markers.map(marker => marker.kind))];
  if (kinds.length <= 1) {
    return null;
  }
  return (
    <ul className={css.poiMapLegend} aria-label="Map legend">
      {kinds.map(kind => (
        <li key={kind} className={css.poiMapLegendItem}>
          <span
            className={css.poiMapLegendSwatch}
            style={{ backgroundColor: POI_MARKER_COLORS[kind] || '#666666' }}
          />
          {POI_MARKER_LABELS[kind] || kind}
        </li>
      ))}
    </ul>
  );
};

PoiMapLegend.propTypes = {
  markers: array.isRequired,
};

const SeoPoiMap = props => {
  const { venue, venueFacts, subpageSlug } = props;
  const config = useConfiguration();
  const sectionRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [isVisible, setIsVisible] = useState(false);
  // Mapbox GL now loads with `defer`, so it may not be ready when this lazy (scroll-in)
  // map first tries to initialize. Track readiness so the init effect re-runs once loaded.
  const [mapLibReady, setMapLibReady] = useState(() => !!isMapsLibLoaded());

  const markers = useMemo(
    () => buildPoiMarkersForSubpage(venue, venueFacts, subpageSlug),
    [venue, venueFacts, subpageSlug]
  );
  const showMap = shouldShowPoiMap(subpageSlug, venue, venueFacts);

  useEffect(() => {
    if (!showMap) {
      return undefined;
    }
    const section = sectionRef.current;
    if (!section || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px 0px', threshold: 0.01 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, [showMap]);

  useEffect(() => {
    if (mapLibReady || !showMap || !isVisible) {
      return undefined;
    }
    let elapsed = 0;
    const intervalMs = 150;
    const timeoutMs = 15000;
    const intervalId = window.setInterval(() => {
      elapsed += intervalMs;
      if (isMapsLibLoaded()) {
        setMapLibReady(true);
        window.clearInterval(intervalId);
      } else if (elapsed >= timeoutMs) {
        window.clearInterval(intervalId);
      }
    }, intervalMs);
    return () => window.clearInterval(intervalId);
  }, [mapLibReady, showMap, isVisible]);

  useEffect(() => {
    if (!showMap || !isVisible || !mapContainerRef.current || mapRef.current) {
      return undefined;
    }
    if (!mapLibReady || !isMapsLibLoaded() || config.maps.mapProvider !== 'mapbox') {
      return undefined;
    }

    if (!window.mapboxgl.accessToken) {
      window.mapboxgl.accessToken = config.maps.mapboxAccessToken;
    }

    const map = new window.mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v10',
      center: [markers[0].lng, markers[0].lat],
      zoom: 13,
      scrollZoom: false,
      attributionControl: false,
    });

    map.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    map.addControl(new MultiTouch());
    const resizeFrame = window.requestAnimationFrame(() => map.resize());

    const onLoad = () => {
      map.resize();
      if (markers.length === 1) {
        map.setCenter([markers[0].lng, markers[0].lat]);
        map.setZoom(14);
      } else {
        const bounds = new window.mapboxgl.LngLatBounds();
        markers.forEach(marker => bounds.extend([marker.lng, marker.lat]));
        map.fitBounds(bounds, { padding: 48, maxZoom: 15 });
      }

      markers.forEach(marker => {
        const popup = new window.mapboxgl.Popup({
          offset: 16,
          closeButton: false,
          maxWidth: '240px',
        }).setHTML(popupHtml(marker));

        const mapMarker = new window.mapboxgl.Marker({
          element: createMarkerElement(marker.kind),
          anchor: 'bottom',
        })
          .setLngLat([marker.lng, marker.lat])
          .setPopup(popup)
          .addTo(map);

        markersRef.current.push(mapMarker);
      });
    };

    map.on('load', onLoad);
    mapRef.current = map;

    return () => {
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      window.cancelAnimationFrame(resizeFrame);
      map.remove();
      mapRef.current = null;
    };
  }, [showMap, isVisible, markers, config.maps, mapLibReady]);

  if (!showMap) {
    return null;
  }

  const heading = getPoiMapHeading(subpageSlug);
  const sourceAttribution = venueFacts?.sourceAttribution;

  return (
    <section ref={sectionRef} className={css.poiMapSection} aria-label={heading}>
      <h2 className={css.poiMapHeading}>{heading}</h2>
      <div className={css.poiMapFrame}>
        {isVisible ? (
          <div ref={mapContainerRef} className={css.poiMapCanvas} role="img" aria-label={heading} />
        ) : (
          <div className={css.poiMapPlaceholder} aria-hidden="true">
            Map loads when scrolled into view
          </div>
        )}
      </div>
      <PoiMapLegend markers={markers} />
      <p className={css.poiMapAttribution}>
        Map data ©{' '}
        <a href="https://www.mapbox.com/about/maps/" rel="noopener noreferrer" target="_blank">
          Mapbox
        </a>{' '}
        ©{' '}
        <a href="https://www.openstreetmap.org/copyright" rel="noopener noreferrer" target="_blank">
          OpenStreetMap
        </a>
        {sourceAttribution ? <> · {sourceAttribution}</> : null}
      </p>
    </section>
  );
};

SeoPoiMap.propTypes = {
  venue: object.isRequired,
  venueFacts: object,
  subpageSlug: string.isRequired,
};

export default SeoPoiMap;
